<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { range, cloneDeep, filter, some } from 'lodash-es';
import store from '../store';

export default {
  data() {
    return {
      datePickerEnabled: false,
      ranges: {
        persons: [1, 24],
        adults: [1, 24],
        children: [0, 24],
        duration: [1, 35],
        flex: [0, 7],
        durations: range(1, 36),
        discount: [0, 100],
      },
      periods: [],
      formValid: true,
      snackbar: false,
      saveSuccess: false,
      availableResultTypes: [
        {
          id: 'accommodations',
          label: this.$t('filterEdit.general.resultTypes.accommodations'),
        },
        {
          id: 'accommodation_categories',
          label: this.$t('filterEdit.general.resultTypes.accommodationCategories'),
        },
      ],
    };
  },
  computed: {
    ...mapState({
      availableBrands: state => state.app.auth.brands,
      availableSuppliers: state => state.app.auth.suppliers,
      brands: state => state.filter.single.general.brands,
      suppliers: state => state.filter.single.general.suppliers,
      id: state => state.filter.single.general.id,
      name: state => state.filter.single.general.name,
      parameters: state => state.filter.single.general.parameters,
      product: state => state.filter.single.general.product,
      rules: state => state.filter.single.general.rules,
      user: state => state.filter.single.general.user,
      changed: state => state.filter.single.general.changed,
      loading: state => state.filter.singleLoading,
    }),
    ...mapGetters({
      countries: 'filter/countries',
      areas: 'filter/areas',
    }),
    validation() {
      return {
        defaultDate: [
          this.dateWithinPeriods,
        ],
        defaultDuration: [
          v => v >= this.rules.minDuration || this.$t('filterEdit.general.validation.defaultDuration.min'),
          v => v <= this.rules.maxDuration || this.$t('filterEdit.general.validation.defaultDuration.max'),
          ],
          defaultAdults: [
          v => v >= this.rules.minAdults || 'Default adults has to be more than or equal to the selected minimum adults',
          v => v <= this.rules.maxAdults || 'Default adults has to be lass than or equal to the selected maximum adults',
        ],
        brands: [
          v => !!v || this.$t('filterEdit.general.validation.brands.required'),
        ],
        name: [
          v => !!v || this.$t('filterEdit.general.validation.name.required'),
        ],
        periodFrom: [
          v => !!v || this.$t('filterEdit.general.validation.periodFrom.required'),
        ],
        periodTo: [
          v => !!v || this.$t('filterEdit.general.validation.periodTo.required'),
        ],
        persons: [
          this.personsNotInRange,
        ],
      };
    },
    defaultAdultsRange() {
      const maxAdults = this.rules.maxAdults + 1;
      return range(this.rules.minAdults, maxAdults);
    },
    personsNotInRange() {
      return ((this.rules.maxAdults + this.rules.maxChildren >= this.rules.minPersons))
        || this.$t('filterEdit.general.validation.persons.range');
    },
    filteredSuppliers() {
      return filter(this.availableSuppliers, s => s.product === this.product.id);
    },
    dateWithinPeriods() {
      if (!this.periods.length) return true;
      return this.allowedDate(this.parameters.date)
        || this.$t('filterEdit.general.validation.date.withinPeriods');
    },
  },
  mounted() {
    this.periods = cloneDeep(this.rules.periods);
  },
  methods: {
    ...mapActions({
      setFilterRules: 'filter/setFilterRules',
      setFilterParameters: 'filter/setFilterParameters',
      setFilterBrands: 'filter/setFilterBrands',
      setFilterSuppliers: 'filter/setFilterSuppliers',
      setFilterName: 'filter/setFilterName',
      saveFilter: 'filter/saveFilter',
    }),
    allowedDate(val) {
      if (this.periods.length) {
        return some(this.periods, period => this.$moment(val)
          .isBetween(period.from, period.to, null, '[]') || this.$moment(val).isSame(period.from, 'day'));
      }
      return true;
    },
    setSliderRule(values, minKey, maxKey) {
      this.setFilterRules({
        [minKey]: values[0],
        [maxKey]: values[1],
      });
      this.$refs.editForm.validate();
    },
    setRule(value, key) {
      const payload = {
        [key]: value,
      };

      if (key === 'enabledCountry' && !this.parameters.country) {
        payload.enabledArea = false;
      }

      if (key === 'enabledCountry' && this.parameters.area) {
        payload.enabledArea = true;
      }

      this.setFilterRules(payload);
    },
    setParameter(value, key) {
      this.setFilterParameters({
        [key]: value,
      });
    },
    setDefaultDate(date) {
      this.setFilterParameters({
        date,
      });
      this.datePickerEnabled = false;
    },
    addPeriod() {
      this.periods.push({
        from: '',
        to: '',
        toOpen: false,
      });
    },
    removePeriod(period) {
      this.periods.splice(this.periods.indexOf(period), 1);
      this.$refs.editForm.validate();
    },
    savePeriods() {
      this.periods = this.periods.filter(period => !!period.from && !!period.to);
      const formattedPeriods = this.periods.map(period => ({
        from: this.$moment(period.from).format('YYYY-MM-DD'),
        to: this.$moment(period.to).format('YYYY-MM-DD'),
        toOpen: false,
      }));
      this.setFilterRules({
        periods: [...formattedPeriods],
      });
    },
    setCountryParameter(value) {
      this.setFilterRules({
        enabledCountry: true,
        enabledArea: true,
      });
      this.setFilterParameters({
        country: value,
        area: null,
      });
    },
    setAreaParameter(value) {
      this.setFilterRules({
        enabledCountry: true,
        enabledArea: true,
      });
      this.setFilterParameters({
        area: value,
      });
    },
    validateAndSaveFilter() {
      if (this.$refs.editForm.validate()) {
        this.savePeriods();
        this.saveFilter(this.$route.params.id)
          .then(() => {
            this.saveSuccess = true;
            this.snackbar = true;
          })
          .catch(() => {
            this.saveSuccess = false;
            this.snackbar = true;
          });
      }
    },
    setToDateAndOpen(date, period) {
      const foundPeriod = this.periods[this.periods.indexOf(period)];

      if (foundPeriod) {
        if (!foundPeriod.to || this.$moment(date).isAfter(period.to)) {
          foundPeriod.to = date;
        }
        foundPeriod.toOpen = true;
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    store.dispatch('filter/fetchFilter', to.params.id)
      .then(() => {
        next();
      });
  },
  beforeRouteLeave(to, from, next) {
    return this.$changeDetect(this.changed, next);
  },
};
</script>

<template>
  <div class="filter-edit-restrictions">
    <v-form ref="editForm" v-model="formValid" class="pb-5">
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('filterEdit.general.labels.name') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('filterEdit.general.labelDescriptions.name') }}
        </div>
        <v-text-field
          :label="$t('filterEdit.general.labels.name')"
          :rules="validation.name"
          :disabled="loading"
          :value="name"
          @change="setFilterName"/>
      </div>
      <v-divider/>
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('filterEdit.general.labels.brands') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('filterEdit.general.labelDescriptions.brands') }}
        </div>
        <v-select
          :value="brands[0]"
          :items="availableBrands"
          :rules="validation.brands"
          :disabled="loading"
          item-value="id"
          item-text="name"
          @change="setFilterBrands([$event])"/>
      </div>
      <v-divider/>
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('filterEdit.general.labels.suppliers') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('filterEdit.general.labelDescriptions.suppliers') }}
        </div>
        <v-select
          multiple
          clearable
          :placeholder="$t('filterEdit.general.labels.suppliers-all')"
          :value="suppliers"
          :items="filteredSuppliers"
          :rules="validation.suppliers"
          :disabled="loading"
          item-value="id"
          item-text="name"
          @change="setFilterSuppliers"/>
      </div>
      <v-divider/>
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('filterEdit.general.labels.resultType') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('filterEdit.general.labelDescriptions.resultType') }}
        </div>
        <v-select
          :value="rules.resultType"
          :items="availableResultTypes"
          :disabled="loading"
          item-value="id"
          item-text="label"
          @change="setRule($event, 'resultType')"/>
      </div>
      <v-divider/>
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('filterEdit.general.labels.flex') }}
        </div>
        <div class="body-1 mb-3">
          Choose how many dates forward and backwards we should flex. Choosing a flex value of
          <span class="font-weight-medium">3</span> and an arrival date of
          <span class="font-weight-medium">2019-03-10</span> will display results for dates
          between <span class="font-weight-medium">2019-03-07</span> and
          <span class="font-weight-medium">2019-03-13</span>.
        </div>
        <v-layout row align-center>
          <v-flex class="pr-2">
            <v-slider
              :value="parameters.flex"
              :label="$t('filterEdit.general.labels.flex')"
              :disabled="loading"
              thumb-label
              prepend-icon="date_range"
              :min="ranges.flex[0]"
              :max="ranges.flex[1]"
              @change="setParameter($event, 'flex')"/>
          </v-flex>
          <v-flex
            class="filter-edit-restrictions__slider-value subheading"
            shrink>
            {{ parameters.flex }}
          </v-flex>
        </v-layout>
        <div v-if="parameters.flex > 3" class="body-1 mt-1">
        <span class="font-weight-medium">
          <span class="orange--text">
            {{ $t('filterEdit.general.labelDescriptions.flexWarningLabel') }}
          </span>
          {{ $t('filterEdit.general.labelDescriptions.flexWarning') }}
        </span>
        </div>
      </div>
      <v-divider/>
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('filterEdit.general.labels.discount') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('filterEdit.general.labelDescriptions.discount') }}
        </div>
        <v-layout row align-center>
          <v-flex class="pr-2">
            <v-range-slider
              :label="$t('filterEdit.general.labels.discount')"
              :disabled="loading"
              :value="[rules.minDiscount, rules.maxDiscount]"
              :min="ranges.discount[0]"
              :max="ranges.discount[1]"
              thumb-label
              prepend-icon="loyalty"
              @change="setSliderRule($event, 'minDiscount', 'maxDiscount')"/>
          </v-flex>
          <v-flex
            class="filter-edit-restrictions__slider-value
            filter-edit-restrictions__slider-value--large
            subheading"
            grow>
            {{ rules.minDiscount }}
            -
            {{ rules.maxDiscount }} %
          </v-flex>
        </v-layout>
      </div>
      <v-divider/>
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('filterEdit.general.labels.intervalRestrictions') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('filterEdit.general.labelDescriptions.intervalRestrictions') }}
        </div>
        <v-layout row align-center>
          <v-flex class="pr-2">
            <v-range-slider
              :label="$t('filterEdit.general.labels.persons')"
              thumb-label
              prepend-icon="group"
              :disabled="loading"
              :value="[rules.minPersons, rules.maxPersons]"
              :rules="validation.persons"
              :min="ranges.persons[0]"
              :max="ranges.persons[1]"
              @change="setSliderRule($event, 'minPersons', 'maxPersons')"/>
          </v-flex>
          <v-flex
            class="filter-edit-restrictions__slider-value subheading"
            shrink>
            {{ rules.minPersons }}
            -
            {{ rules.maxPersons }}
          </v-flex>
        </v-layout>
        <v-layout row align-center>
          <v-flex class="pr-2">
            <v-range-slider
              :label="$t('filterEdit.general.labels.adults')"
              prepend-icon="person"
              thumb-label
              :disabled="loading"
              :value="[rules.minAdults, rules.maxAdults]"
              :min="ranges.adults[0]"
              :max="ranges.adults[1]"
              @change="setSliderRule($event, 'minAdults', 'maxAdults')"/>
          </v-flex>
          <v-flex
            class="filter-edit-restrictions__slider-value subheading"
            shrink>
            {{ rules.minAdults }}
            -
            {{ rules.maxAdults }}
          </v-flex>
        </v-layout>
        <v-layout row align-center>
          <v-flex class="pr-2">
            <v-slider
              :label="$t('filterEdit.general.labels.children')"
              prepend-icon="face"
              thumb-label
              :disabled="loading"
              :value="rules.maxChildren"
              :min="ranges.children[0]"
              :max="ranges.children[1]"
              @change="setRule($event, 'maxChildren')"/>
          </v-flex>
          <v-flex
            class="filter-edit-restrictions__slider-value subheading"
            shrink>
            {{ rules.minChildren }}
            -
            {{ rules.maxChildren }}
          </v-flex>
        </v-layout>
        <v-layout row align-center>
          <v-flex class="pr-2">
            <v-range-slider
              :label="$t('filterEdit.general.labels.durations')"
              prepend-icon="calendar_today"
              thumb-label
              :disabled="loading"
              :value="[rules.minDuration, rules.maxDuration]"
              :min="ranges.duration[0]"
              :max="ranges.duration[1]"
              @change="setSliderRule($event, 'minDuration', 'maxDuration')"/>
          </v-flex>
          <v-flex
            class="filter-edit-restrictions__slider-value  subheading"
            shrink>
            {{ rules.minDuration }}
            -
            {{ rules.maxDuration }}
          </v-flex>
        </v-layout>
      </div>
      <v-divider/>
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('filterEdit.general.labels.fieldRestrictions') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('filterEdit.general.labelDescriptions.fieldRestrictions') }}
        </div>
        <div class="caption mb-1 grey--text">
          <strong>1)</strong>
          {{ $t('filterEdit.general.labelDescriptions.fieldRestrictionsRule1') }}
        </div>
        <div class="caption mb-3 grey--text">
          <strong>2)</strong>
          {{ $t('filterEdit.general.labelDescriptions.fieldRestrictionsRule2') }}
        </div>
        <v-checkbox
          :input-value="rules.enabledCountry"
          :label="$t('filterEdit.general.labels.country')"
          :disabled="loading"
          hide-details
          @change="setRule($event, 'enabledCountry')"/>
        <v-checkbox
          :input-value="rules.enabledArea"
          :label="$t('filterEdit.general.labels.area')"
          :disabled="loading
          || (!parameters.country && !rules.enabledCountry)
          || (parameters.area && rules.enabledCountry)"
          hide-details
          @change="setRule($event, 'enabledArea')"/>
        <v-checkbox
          :input-value="rules.enabledDate"
          :label="$t('filterEdit.general.labels.date')"
          :disabled="loading"
          hide-details
          @change="setRule($event, 'enabledDate')"/>
        <v-checkbox
          :input-value="rules.enabledDuration"
          :disabled="loading"
          :label="$t('filterEdit.general.labels.duration')"
          hide-details
          @change="setRule($event, 'enabledDuration')"/>
        <div class="caption mt-4">
          <strong class="orange--text">Note:</strong>
          <span class="grey--text">
          {{ $t('filterEdit.general.labelDescriptions.fieldRestrictionsNotice') }}
          </span>
        </div>
      </div>
      <v-divider/>
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('filterEdit.general.labels.filterRestrictions') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('filterEdit.general.labelDescriptions.filterRestrictions') }}
        </div>
        <v-checkbox
          :input-value="rules.enabledCategories"
          :disabled="loading"
          :label="$t('filterEdit.general.labels.categories')"
          hide-details
          @change="setRule($event, 'enabledCategories')"/>
        <v-checkbox
          :input-value="rules.enabledBedrooms"
          :disabled="loading"
          :label="$t('filterEdit.general.labels.bedrooms')"
          hide-details
          @change="setRule($event, 'enabledBedrooms')"/>
        <v-checkbox
          :input-value="rules.enabledFacilities"
          :disabled="loading"
          :label="$t('filterEdit.general.labels.siteFacilities')"
          hide-details
          @change="setRule($event, 'enabledFacilities')"/>
      </div>
      <v-divider/>
      <div class="py-4 px-4">
        <v-layout row justify-space-between align-center class="mb-3">
          <div class="title">
            {{ $t('filterEdit.general.labels.periods') }}
          </div>
          <v-btn
            flat
            :disabled="loading"
            color="success"
            class="ma-0"
            @click="addPeriod">
            <v-icon dark class="mr-2">add</v-icon>
            {{ $t('buttons.addPeriod') }}
          </v-btn>
        </v-layout>
        <div class="mb-3">
          {{ $t('filterEdit.general.labelDescriptions.periods') }}
        </div>
        <div v-if="!periods.length" class="caption font-italic">
          {{ $t('filterEdit.general.warnings.noDatePeriods') }}
        </div>
        <v-layout
          v-for="(period, index) in periods"
          :key="'period-' + index"
          row
          align-center>
          <v-flex xs6 class="pr-3">
            <v-menu
              lazy
              offset-y
              max-width="290px"
              min-width="290px">
              <template v-slot:activator="{ on }">
                <v-text-field
                  :label="$t('filterEdit.general.labels.dateFrom')"
                  :rules="validation.periodFrom"
                  :disabled="loading"
                  :value="period.from"
                  v-on="on"/>
              </template>
              <v-date-picker
                v-model="period.from"
                :show-current="false"
                :first-day-of-week="1"
                no-title
                @input="setToDateAndOpen($event, period)"/>
            </v-menu>
          </v-flex>
          <v-icon>arrow_right_alt</v-icon>
          <v-flex xs6 class="pl-3">
            <v-menu
              v-model="period.toOpen"
              lazy
              offset-y
              max-width="290px"
              min-width="290px">
              <template v-slot:activator="{ on }">
                <v-text-field
                  :label="$t('filterEdit.general.labels.dateTo')"
                  :disabled="loading"
                  :rules="validation.periodTo"
                  :value="period.to"
                  v-on="on"/>
              </template>
              <v-date-picker
                v-model="period.to"
                :open="true"
                :show-current="false"
                :min="period.from"
                :first-day-of-week="1"
                no-title/>
            </v-menu>
          </v-flex>
          <v-btn
            flat
            icon
            color="error"
            @click="removePeriod(period)"
            class="ml-4 mr-0">
            <v-icon small>delete</v-icon>
          </v-btn>
        </v-layout>
      </div>
      <v-divider/>
      <div class="pt-4 pb-5 px-4">
        <div class="title mb-3">
          {{ $t('filterEdit.general.labels.parameters') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('filterEdit.general.labelDescriptions.parameters') }}
        </div>
        <v-layout row>
          <v-flex xs6 class="pr-2">
            <v-select
              :label="$t('filterEdit.general.labels.country')"
              prepend-icon="place"
              :disabled="loading"
              :items="countries"
              :value="parameters.country"
              @change="setCountryParameter($event)"/>
          </v-flex>
          <v-flex xs6 class="pl-2">
            <v-select
              :label="$t('filterEdit.general.labels.area')"
              prepend-icon="place"
              :items="areas"
              :disabled="!parameters.country || loading"
              :value="parameters.area"
              @change="setAreaParameter($event)"/>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs6 class="pr-2">
            <v-menu
              v-model="datePickerEnabled"
              :close-on-content-click="false"
              lazy
              offset-y
              full-width
              max-width="290px"
              min-width="290px">
              <template v-slot:activator="{ on }">
                <v-text-field
                  :label="$t('filterEdit.general.labels.date')"
                  :value="parameters.date"
                  :disabled="loading"
                  readonly
                  :rules="validation.defaultDate"
                  prepend-icon="event"
                  v-on="on"/>
              </template>
              <v-date-picker
                :value="parameters.date"
                :first-day-of-week="1"
                :allowed-dates="allowedDate"
                no-title
                @input="setDefaultDate"/>
            </v-menu>
          </v-flex>
          <v-flex xs6 class="pl-2">
            <v-select
              :label="$t('filterEdit.general.labels.duration')"
              prepend-icon="date_range"
              :rules="validation.defaultDuration"
              :disabled="loading"
              :items="ranges.durations"
              :value="parameters.duration"
              @change="setParameter($event, 'duration')"/>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs6 class="pr-2">
            <v-select
              label="Adults"
              prepend-icon="person"
              :rules="validation.defaultAdults"
              :disabled="loading"
              :items="defaultAdultsRange"
              :value="parameters.persons.adults"
              @change="setParameter({ adults: $event, children: parameters.persons.children }, 'persons')"/>
          </v-flex>
      </div>
    </v-form>
    <v-tooltip left :disabled="formValid">
      <template v-slot:activator="{ on }">
        <v-btn
          fixed
          bottom
          right
          v-on="on"
          :loading="loading"
          color="primary"
          @click="validateAndSaveFilter">
          Save
        </v-btn>
      </template>
      <span v-if="!formValid">The form is not valid. Please check for warnings</span>
    </v-tooltip>
    <v-snackbar
      v-model="snackbar"
      bottom
      :timeout="4000">
      <v-icon v-if="saveSuccess" class="mr-3 green--text">check_circle</v-icon>
      <v-icon v-else class="mr-3 red--text">warning</v-icon>
      {{ saveSuccess
      ? $t('filterEdit.general.snackbar.save.success')
      : $t('filterEdit.general.snackbar.save.error')
      }}
      <v-btn
        color="white"
        flat
        @click="snackbar = false">
        {{ $t('filterEdit.general.snackbar.save.button') }}
      </v-btn>
    </v-snackbar>
  </div>
</template>

<style lang="scss">
  .filter-edit-restrictions {
    &__slider-value {
      width: 80px;
      text-align: right;

      &--large {
        width: 100px;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/FilterEditGeneral.vue